import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { SectionType, StudentSection } from 'libs/domain';
import { BehaviorSubject, combineLatest, from, Observable, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { AuthInfoService } from '@app/src/app/minimal/services/AuthInfo';
import { BellSchedulePermissionsService } from '@app/src/app/modules/minga-manager/components/mm-bell-schedule/services/bell-schedule-permissions.service';
import { PermissionsService } from '@app/src/app/permissions';
import { HallPassService } from '@app/src/app/services/HallPass';
import { OptionItem } from '@app/src/app/shared/components/form/components/form-grouped-select/form-grouped-select.types';
import { SystemAlertSnackBarService } from '@app/src/app/shared/components/system-alert-snackbar';
import { UserListCategory, UserListFilterService } from '@app/src/app/shared/components/user-list-filter';
import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';
import { FlexTimePeriodService, FlexTimePermissionsService } from '@app/src/app/shared/services/flex-time';
import { StudentScheduleService } from '@app/src/app/shared/services/student-schedule';
import { getToolsScheduleRoute } from '@app/src/app/shared/services/student-schedule/utils/student-schedule.utils';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { PeopleViewProfilePerson } from '@modules/people/components/people-view-profile/types';
import { ToolsActionsService } from '@modules/tools/services/tools.actions.service';
import { ACTION_CATEGORY, ACTION_KEY } from '@modules/tools/shared-tools/types/shared-tools.types';

import { getAppColor } from '@shared/constants';
import { MingaStats } from '@shared/services/stats/stats.types';

import { AdminToolsMessages } from '../../../admin-tools/admin-tools.constant';
import { StudentDashboardAbstract } from '../../../shared-tools/utils/student-dashboard-abstract.utils';
import { StudentToolsFlexTimeService } from '../../../student-tools/services';
import { ToolsRoutes } from '../../../tools.types';
import { MyClassQueryParams } from '../tt-my-class/constants/tt-my-class.constants';

@Component({
  selector: 'mg-tt-my-tools',
  templateUrl: './tt-my-tools.component.html',
  styleUrls: ['./tt-my-tools.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToolsActionsService, QueryParamService],
})
export class TtMyToolsComponent extends StudentDashboardAbstract implements OnDestroy {
  // Constants

  public readonly MESSAGES = AdminToolsMessages;

  // Observables

  private _destroyedSubject = new ReplaySubject<void>(1);
  private _loadingStatsSubject = new BehaviorSubject<boolean>(true);
  public loadingStats$ = this._loadingStatsSubject.asObservable();

  private _mingaStatsSubject = new BehaviorSubject<MingaStats>(null);
  public mingaStats$ = this._mingaStatsSubject.asObservable();

  public isGroupsEnabled$ = this.mingaSettings.isCommunityModuleEnabled();
  public hasHomeroom$ = from(this._userListFilter.getMyHomeroomLists()).pipe(
    map(homerooms => homerooms.length > 0),
  );

  public assignActions = this._toolsActions.actions
    .filter(a => a.usedIn.includes('teacher'))
    .filter(a =>
      [ACTION_CATEGORY.HALL_PASS, ACTION_CATEGORY.CHECK_IN, ACTION_CATEGORY.ACTIVITY, ACTION_CATEGORY.PBIS].includes(
        a.category,
      ),
    );

  public hasAssignActions$: Observable<boolean> = combineLatest(
    this.assignActions.map(action => action.hasPermission$),
  ).pipe(map(permissions => permissions.some(hasPermission => hasPermission)));

  public pointsActions = this._toolsActions.actions
    .filter(a => a.usedIn.includes('teacher'))
    .filter(a => a.category === ACTION_CATEGORY.POINTS);

  public hasPointsActions$: Observable<boolean> = combineLatest(
    this.pointsActions.map(action => action.hasPermission$),
  ).pipe(map(permissions => permissions.some(hasPermission => hasPermission)));

  public kioskAction = this._toolsActions.actions.find(a => a.key === ACTION_KEY.KIOSK_MODE);

  public person$: Observable<PeopleViewProfilePerson> = this.authInfoService.authPerson$.pipe(
    map(p => {
      if (!p) return null;

      return {
        hash: p.hash,
        badgeRoleName: p.badgeRoleName,
        firstName: p.firstName,
        lastName: p.lastName,
        profileImageUrl: p.avatarUrl,
        grade: p.grade,
        studentId: p.studentId,
        filename: p.filename,
      };
    }),
  );

  public favoriteActions$ = this._toolsActions.favoriteActions$;

  public ICON_COLORS = {
    assign: getAppColor('secondary-600'),
    points: getAppColor('warning-300'),
    kiosk: getAppColor('surface'),
  };

  constructor(
    public appConfig: AppConfigService,
    private _toolsActions: ToolsActionsService,
    private _router: Router,
    private _userListFilter: UserListFilterService,
    private _queryParamService: QueryParamService,
    // ... inject services from the abstract class ...
    public hallPassService: HallPassService,
    public studentScheduleService: StudentScheduleService,
    public studentFlexTime: StudentToolsFlexTimeService,
    public mingaSettings: MingaSettingsService,
    public snackBarService: SystemAlertSnackBarService,
    public permissions: PermissionsService,
    public flexPermissions: FlexTimePermissionsService,
    public bellSchedulePermissions: BellSchedulePermissionsService,
    public flexPeriod: FlexTimePeriodService,
    public authInfoService: AuthInfoService,
  ) {
    super(
      hallPassService,
      studentScheduleService,
      studentFlexTime,
      mingaSettings,
      snackBarService,
      appConfig,
      permissions,
      flexPermissions,
      bellSchedulePermissions,
      flexPeriod,
      authInfoService,
    );
  }

  // Lifecycle Hooks

  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  // Abstract Methods

  protected onInit(): void {
    const authHash = this.authInfoService.authPersonHash;
    this._fetchStudentData(authHash, false);
  }

  // Public Methods

  public goToSchedule() {
    const route = getToolsScheduleRoute(SectionType.BELL);
    this._router.navigate(route);
  }

  public openMyClassWithScheduleItem(event: { isFlexActivity: boolean; schedule?: StudentSection }) {
    const userlist: OptionItem = {
      category: {
        value: event.isFlexActivity ? UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE : UserListCategory.ALL,
        label: event.isFlexActivity ? UserListCategory.TODAYS_FLEX_ACTIVITIES_MINE : UserListCategory.ALL,
      },
      data: {
        id: event.schedule.id,
        title: event.schedule.name,
        currentTerm: true,
        isHomeroom: false,
        flexTimeActivity: {
          name: event.schedule.name,
          showProfilePicture: event.schedule.showProfilePicture ?? false,
        },
        checkinReasonId: event.schedule.checkinReasonId,
      },
      label: event.schedule.name,
      value: event.schedule.id,
    };

    this._queryParamService.updateQueryParams<MyClassQueryParams>(
      {
        lists: [userlist],
      },
      {},
      [ToolsRoutes.ROOT, ToolsRoutes.MY_CLASS],
    );
  }

  public async openMyClassWithHomeroom() {
    const homerooms = await this._userListFilter.getMyHomeroomLists();
    const homeroomOptionItems = homerooms.map(item => ({
      ...item,
      category: {
        value: 'homeroom',
      },
    }));
    this._queryParamService.updateQueryParams<MyClassQueryParams>(
      {
        lists: homeroomOptionItems,
      },
      {},
      [ToolsRoutes.ROOT, ToolsRoutes.MY_CLASS],
    );
  }
}
